import {TimeZone} from '@hconnect/common/types'
import {timeRangeParamToDateRange} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {SelectionItem} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/types'
import {TFunction} from 'i18next'
import moment, {Moment} from 'moment-timezone'

import {LOCATION_QUICK_SELECTIONS, PLANT_QUICK_SELECTIONS} from '../consts'
import {QuickSelectionId} from '../types'

const isLocationStatusTimeRange = (value: string | undefined): value is QuickSelectionId =>
  Object.values(LOCATION_QUICK_SELECTIONS).includes(value as QuickSelectionId)

const isPlantStatusTimeRange = (value: string | undefined): value is QuickSelectionId =>
  Object.values(PLANT_QUICK_SELECTIONS).includes(value as QuickSelectionId)

export const getQuickSelectionItems = (
  t: TFunction,
  timezone: TimeZone,
  now = new Date()
): SelectionItem<QuickSelectionId>[] => {
  const startOfToday: Moment = moment.utc(now).tz(timezone).startOf('day')
  const endOfToday: Moment = moment.utc(now).tz(timezone).startOf('minute')

  const startOfYesterday: Moment = startOfToday.clone().subtract(1, 'day')
  const endOfYesterday: Moment = startOfYesterday.clone().endOf('day')

  const currentMonth = startOfToday.clone().startOf('month').month()

  const quickSelectItems: SelectionItem<QuickSelectionId>[] = []

  quickSelectItems.push(
    {
      id: 'today',
      title: t('quickSelection.today'),
      dates: [startOfToday.toDate(), endOfToday.toDate()]
    },
    {
      id: 'yesterday',
      title: t('quickSelection.yesterday'),
      dates: [startOfYesterday.toDate(), endOfYesterday.toDate()]
    },
    {
      id: 'last7days',
      title: t('quickSelection.last7days'),
      dates: [startOfToday.clone().subtract(7, 'day').toDate(), endOfYesterday.toDate()]
    },
    {
      id: 'currentMonth',
      title: t('quickSelection.currentMonth'),
      dates: [startOfToday.clone().startOf('month').toDate(), endOfToday.toDate()]
    },
    {
      id: 'lastMonth',
      title: t('quickSelection.lastMonth'),
      dates: [
        startOfToday.clone().subtract(1, 'month').startOf('month').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    }
  )

  if (currentMonth !== 0) {
    quickSelectItems.push({
      id: 'yearToMonthYTM',
      title: t('quickSelection.yearToMonthYTM'),
      dates: [
        startOfToday.clone().startOf('year').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    })
  }

  quickSelectItems.push(
    {
      id: 'last3months',
      title: t('quickSelection.last3months'),
      dates: [
        startOfToday.clone().subtract(3, 'month').startOf('month').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    },
    {
      id: 'yearToDateYTD',
      title: t('quickSelection.yearToDateYTD'),
      dates: [startOfToday.clone().startOf('year').toDate(), endOfToday.toDate()]
    },
    {
      id: 'lastYear',
      title: t('quickSelection.lastYear'),
      dates: [
        startOfToday.clone().subtract(1, 'year').startOf('year').toDate(),
        startOfToday.clone().subtract(1, 'year').endOf('year').toDate()
      ]
    }
  )

  return quickSelectItems
}

export const getLocationStatusQuickSelectionItems = (t: TFunction, timezone: TimeZone) =>
  getQuickSelectionItems(t, timezone).filter(({id}) => isLocationStatusTimeRange(id))

export const getPlantStatusQuickSelectionItems = (t: TFunction, timezone: TimeZone) =>
  getQuickSelectionItems(t, timezone).filter(({id}) => isPlantStatusTimeRange(id))

const getDefaultSelectionItem = (
  selectionItems: SelectionItem<QuickSelectionId>[],
  timezone: TimeZone,
  defaultSelectionId: QuickSelectionId
) => {
  const item = selectionItems.find((item) => item.id === defaultSelectionId)

  if (!item) throw new Error(`Default selection item "${defaultSelectionId}" not found`)

  const start = item.dates[0]
  const defaultEnd = moment.utc().tz(timezone).startOf('minute').toDate()
  const end = item.dates[1] ?? defaultEnd
  const quickSelectionId = item.id

  return [start, end, quickSelectionId] as const
}

type ParseTimeRangeParams = {
  selectionItems: SelectionItem<QuickSelectionId>[]
  timeRange: string
  timezone: TimeZone
  defaultSelectionId: QuickSelectionId
}

export const parseTimeRangeParamToDateRange = ({
  selectionItems,
  timeRange,
  timezone,
  defaultSelectionId
}: ParseTimeRangeParams) => {
  const [defaultStart, defaultEnd, defaultQuickSelectionId] = getDefaultSelectionItem(
    selectionItems,
    timezone,
    defaultSelectionId
  )
  const [start, end, quickSelectionId] = timeRangeParamToDateRange({timeRange, selectionItems})
  const isCustomDateRange = start && end && !quickSelectionId

  return {
    from: start ?? defaultStart,
    to: end ?? defaultEnd,
    quickSelectionId: isCustomDateRange ? null : (quickSelectionId ?? defaultQuickSelectionId)
  }
}
